import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { type SendReminderVariables, SendReminderVariablesSchema } from './send-reminder-variables';

export type SendAdditionalReminderVariables = SendReminderVariables;

async function sendAdditionalReminderFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: SendAdditionalReminderVariables,
): Promise<ApiResponse> {
  const {
    invoiceId,
    additionalInvoiceIds,
    creditNoteIds,
    accountingPaymentIds,
    tpPaymentIds,
    sendingOptions,
  } = SendReminderVariablesSchema.parse(variables);
  const instance = await axiosInstance;

  return instance.post(`invoices/${invoiceId}/additional_reminder`, {
    ...sendingOptions,
    additionalInvoices: additionalInvoiceIds,
    creditNotes: creditNoteIds,
    accountingPayments: accountingPaymentIds,
    tpPayments: tpPaymentIds,
  });
}

export function useSendAdditionalReminder() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, SendAdditionalReminderVariables>({
    mutationFn: variables => sendAdditionalReminderFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['unhandledClient'] });
      client.invalidateQueries({ queryKey: ['unhandledClients'] });
      client.invalidateQueries({ queryKey: ['unhandled-debtors-view'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    SendAdditionalReminderVariables,
    'sendAdditionalReminder'
  >('sendAdditionalReminder', mutation);
}
